import { Breakpoints, Palette, PaletteMode, ThemeOptions } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { typographyClasses } from '@mui/material/Typography';
import createBreakpoints from '@mui/system/createTheme/createBreakpoints';
import deepmerge from 'deepmerge';
import React from 'react';
import { SkodaAnimatedCheckboxIcon, SkodaExpandMoreIcon, SkodaRadioAnimatedIcon } from '../components/icons';
import { SkodaSelectMenu } from '../components/scrollbars/scrollbars';
import { fabExtended, fabPrimary, fabRoot, fabSecondary } from './components/fab';
import { menuItemRoot, menuItemRootDark } from './components/menuItem';
import { sliderOverrides } from './components/slider';
import { tooltipOverrides } from './components/tooltip';
import { resolvePalette } from './palette';
import './themeExtensions';

export const defaultSkodaBreakpoints = createBreakpoints({
	values: {
		xs: 0,
		sm: 576,
		md: 992,
		lg: 1200,
		xl: 1920
	}
});

const createBaseSkodaTheme = (palette: Palette, breakpoints: Breakpoints, originalBreakpoints: Breakpoints): ThemeOptions => ({
	breakpoints,
	originalBreakpoints,
	palette,
	typography: {
		htmlFontSize:
			+window.getComputedStyle(document.documentElement).fontSize
				.replace('px', ''),
		fontFamily: 'inherit',
		allVariants: {
			color: palette.text.primary
		},
		h1: {
			fontWeight: 'bold',
			padding: 0,
			margin: 0,
			textTransform: 'none',
			fontSize: '33px',
			lineHeight: '40px',
			[breakpoints.up('md')]: {
				fontSize: '44px',
				lineHeight: '52px'
			},
			[`&.${typographyClasses.gutterBottom}`]: {
				marginBottom: 50
			}
		},
		h2: {
			fontWeight: 'bold',
			padding: 0,
			margin: 0,
			textTransform: 'none',
			fontSize: '28px',
			lineHeight: '36px',
			[breakpoints.up('md')]: {
				fontSize: '32px',
				lineHeight: '38px'
			},
			[breakpoints.up('lg')]: {
				fontSize: '36px',
				lineHeight: '44px'
			},
			[`&.${typographyClasses.gutterBottom}`]: {
				marginBottom: 40,
				[breakpoints.up('lg')]: {
					marginBottom: 56
				}
			}
		},
		h3: {
			fontWeight: 'bold',
			padding: 0,
			margin: 0,
			textTransform: 'none',
			fontSize: '28px',
			lineHeight: '36px',
			[`&.${typographyClasses.gutterBottom}`]: {
				marginBottom: 28
			}
		},
		fontWeightRegular: 200,
		fontWeightLight: 200,
		fontWeightMedium: 300,
		fontWeightBold: 700
	},
	components: {
		MuiSlider: {
			styleOverrides: {
				...sliderOverrides(palette),
				thumbSizeSmall: {
					width: 16,
					height: 16,
					'&.Mui-disabled': {
						width: 16,
						height: 16,
						marginLeft: 0
					}
				},
				sizeSmall: {
					paddingTop: 6,
					paddingBottom: 6,
					marginRight: 16,
					height: 4,
					'@media (pointer: coarse)': {
						paddingTop: 6,
						paddingBottom: 6
					},
					'& .MuiSlider-rail': {
						right: -16,
						height: 4
					},
					'& .MuiSlider-track': {
						height: 4
					}
				}
			}
		},
		MuiDialog: {
			styleOverrides: {
				root: {
					color: palette.text.primary,
					fontWeight: 200,
					'& ::selection': {
						background: palette.primary.main,
						color: '#ffffff'
					},
					'&::selection': {
						background: palette.primary.main,
						color: '#ffffff'
					}
				}
			}
		},
		MuiFab: {
			styleOverrides: {
				root: fabRoot(),
				extended: fabExtended(palette),
				secondary: fabSecondary(palette),
				primary: fabPrimary(palette)
			},
			defaultProps: {
				focusRipple: false
			}
		},
		MuiSelect: {
			styleOverrides: {
				icon: {
					color: palette.dropdownIcon,
					top: '50%',
					transform: 'translateY(-50%)',
					transitionDuration: '0.15s',
					transitionProperty: 'transform'
				},
				iconFilled: {
					right: '21px',
					top: '65%'
				},
				iconOpen: {
					transform: 'translateY(-50%) rotate(180deg)'
				},
				select: {
					'&:focus': {
						backgroundColor: 'unset'
					}
				},
				filled: {
					'&.MuiSelect-filled': {
						paddingRight: 64
					}
				}
			},
			defaultProps: {
				IconComponent: SkodaExpandMoreIcon,
				MenuProps: {
					PaperProps: {
						component: SkodaSelectMenu
					} as any,
					anchorOrigin: {
						vertical: 'bottom',
						horizontal: 'left'
					},
					transformOrigin: {
						vertical: -10,
						horizontal: 'left'
					},
					MenuListProps: {
						disablePadding: true
					}
				},
				autoFocus: false,
				variant: 'standard'
			}
		},
		MuiPopover: {
			styleOverrides: {
				paper: {
					maxHeight: 250
				}
			}
		},
		MuiMenuItem: {
			styleOverrides: {
				root: {
					...menuItemRoot(palette),
					'&.dark': {
						...menuItemRootDark(palette)
					}
				}
			},
			defaultProps: {
				autoFocus: false
			}
		},
		MuiInputBase: {
			styleOverrides: {
				root: {
					fontWeight: 'inherit',
					fontSize: '18px',
					'&.MuiInput-root.Mui-disabled': {
                        ':before': {
                            borderBottomStyle: 'solid',
							borderColor: palette.action.disabled
                        }
                    },
					'&.MuiFilledInput-root.Mui-disabled': {
                        ':before': {
                            borderBottomStyle: 'solid',
							borderColor: palette.action.disabled
                        }
                    }
				},
				input: {
					paddingBottom: 15,
					paddingTop: 15
				}
			}
		},
		MuiInputLabel: {
			styleOverrides: {
				root: {
					fontWeight: 'inherit',
					fontSize: '18px'
				},
				formControl: {
					color: palette.formControlLabel
				},
				filled: {
					transform: 'translate(16px, 26px) scale(1)',
					'&.MuiInputLabel-shrink': {
						transform: 'translate(16px, 12px) scale(0.777)'
					}
				},
				standard: {
					transform: 'translate(0, 26px) scale(1)',
					'&.MuiInputLabel-shrink': {
						transform: 'translate(0, -1.5px) scale(0.777)'
					}
				}
			}
		},
		MuiFormControl: {
			defaultProps: {
				variant: 'standard'
			}
		},
		MuiTextField: {
			defaultProps: {
				variant: 'standard'
			}
		},
		MuiFilledInput: {
			styleOverrides: {
				root: {
					backgroundColor: palette.background.paper,
					borderTopLeftRadius: 0,
					borderTopRightRadius: 0,
					'&:hover': {
						backgroundColor: palette.background.paper
					},
					'&.Mui-focused': {
						backgroundColor: palette.background.paper
					},
					'&:before': {
						borderBottom: '1px solid ' + palette.text.primary
					}
				},
				input: {
					paddingLeft: 16,
					paddingRight: 16
				}
			}
		},
		MuiInput: {
			styleOverrides: {
				root: {
					'&:before': {
						borderBottom: '1px solid ' + palette.text.primary
					}
				},
				formControl: {
					'label + &': {
						marginTop: 11
					}
				}
			}
		},
		MuiButtonBase: {
			styleOverrides: {
				root: {
					outline: 'none !important'
				}
			},
			defaultProps: {
				disableRipple: true
			}
		},
		MuiButton: {
			styleOverrides: {
				root: {
					textTransform: 'none'
				},
				textPrimary: {
					color: palette.primary.main
				},
				textSecondary: {
					color: palette.text.primary
				},
				outlinedSecondary: {
					color: palette.text.primary,
					borderColor: palette.text.primary,
					'&:hover': {
						borderColor: palette.text.primary
					}
				}
			}
		},
		MuiSwitch: {
			styleOverrides: {
				root: {
					width: 76,
					height: 48
				},
				switchBase: {
					top: 1,
					left: 1,
					padding: 14,
					'&.Mui-checked': {
						transform: 'translateX(27px)',
						'& + .MuiSwitch-track': {
							opacity: 1
						}
					}
				},
				track: {
					borderRadius: 12,
					opacity: 1,
					background: palette.text.disabled
				},
				thumb: {
					width: 18,
					height: 18,
					boxShadow: 'none',
					color: palette.background.paper
				},
				colorPrimary: {
					'&.Mui-disabled': {
						'&.Mui-checked': {
							'& + .MuiSwitch-track': {
								backgroundColor: palette.primary.main,
								opacity: 0.5
							}
						}
					}
				},
				colorSecondary: {
					'&.Mui-disabled': {
						'&.Mui-checked': {
							'& + .MuiSwitch-track': {
								opacity: 0.12
							}
						}
					}
				}
			}
		},
		MuiSvgIcon: {
			styleOverrides: {
				root: {
					verticalAlign: 'middle'
				},
				fontSizeSmall: {
					fontSize: '22px'
				}
			}
		},
		MuiIconButton: {
			styleOverrides: {
				root: {
					fontSize: '27px',
					[breakpoints.up('xl')]: {
						fontSize: '31px'
					},
					'@media (hover: hover)': {
						'&:hover': {
							color: palette.primary.main
						}
					},
					'&:active': {
						color: palette.primary.main
					}
				},
				colorPrimary: {
					'@media (hover: hover)': {
						'&:hover': {
							color: palette.primary.main
						}
					},
					'&:active': {
						color: palette.primary.main
					}
				},
				sizeSmall: {
					fontSize: '24px',
					[breakpoints.up('xl')]: {
						fontSize: '28px'
					}
				}
			}
		},
		MuiPaper: {
			defaultProps: {
				square: true
			}
		},
		MuiCheckbox: {
			defaultProps: {
				icon: <SkodaAnimatedCheckboxIcon fontSize='inherit' value='unchecked' />,
				checkedIcon: <SkodaAnimatedCheckboxIcon fontSize='inherit' value='checked' />,
				indeterminateIcon: <SkodaAnimatedCheckboxIcon fontSize='inherit' value='indeterminate' />
			},
			styleOverrides: {
				root: {
					fontSize: '27px'
				}
			}
		},
		MuiRadio: {
			defaultProps: {
				icon: <SkodaRadioAnimatedIcon fontSize='inherit' />,
				checkedIcon: <SkodaRadioAnimatedIcon fontSize='inherit' />
			}
		},
		MuiTooltip: {
			styleOverrides: tooltipOverrides(palette)
		},
		MuiChip: {
			defaultProps: {
				variant: 'filled',
				color: 'primary',
				clickable: true
			},
			styleOverrides: {
				root: {
					height: 32,
					'&:active': {
						boxShadow: 'none'
					}
				},
				label: {
					fontSize: '16px',
					lineHeight: '20px',
					paddingLeft: 16,
					paddingRight: 16,
					fontWeight: 400
				},
				colorPrimary: {
					backgroundColor: palette.chip.backgroundPrimary,
					color: palette.chip.colorPrimary,
					'&:hover': {
						backgroundColor: palette.chip.backgroundPrimary,
						color: palette.chip.colorPrimaryHover
					}
				},
				colorSecondary: {
					backgroundColor: palette.chip.backgroundSecondary,
					color: palette.chip.colorSecondary,
					'&:hover': {
						backgroundColor: palette.chip.backgroundSecondary,
						color: palette.chip.colorSecondaryHover
					}
				},
				outlinedPrimary: {
					backgroundColor: 'transparent',
					color: palette.chip.colorPrimary,
					borderColor: palette.chip.colorPrimary
				},
				outlinedSecondary: {
					backgroundColor: 'transparent',
					color: palette.chip.colorSecondary,
					borderColor: palette.chip.colorSecondary
				},
				deleteIconColorPrimary: {
					color: palette.chip.colorPrimary,
					'&:hover': {
						color: palette.chip.colorPrimaryHover
					}
				},
				deleteIconColorSecondary: {
					color: palette.chip.colorSecondary,
					'&:hover': {
						color: palette.chip.colorSecondaryHover
					}
				},
				deleteIconOutlinedColorPrimary: {
					color: palette.chip.colorPrimary,
					'&:hover': {
						color: palette.chip.colorPrimaryHover
					}
				},
				deleteIconOutlinedColorSecondary: {
					color: palette.chip.colorSecondary,
					'&:hover': {
						color: palette.chip.colorSecondaryHover
					}
				}
			}
		}
	},
	spacing: [0, 4, 8, 12, 15, 20, 24, 30, 40, 60, 80],
	Skoda: {
		settings: {
			headerBanner: {
				color: '#232526'
			},
			skodaSlider: {
				colors: [palette.secondary.main, '#84B5A6', '#3A6558', '#2A5548']
			},
			bannerConfigurator: {
				colors: palette.mode === 'light'
					? [palette.skodaColors['neutrals-white'], '#DBDBDB', '#D9D9D9']
					: [palette.skodaColors['neutrals-chrome900'], palette.skodaColors['neutrals-chrome800'], palette.skodaColors['neutrals-chrome900']]
			}
		}
	}
});

export const createSkodaTheme = (
	mode: PaletteMode,
	themeOverride?: (baseSkodaTheme: ThemeOptions) => ThemeOptions,
	breakpoints?: Breakpoints,
	originalBreakpoints?: Breakpoints) => {
	const palette = resolvePalette(mode);
	const baseTheme = createBaseSkodaTheme(
		palette,
		breakpoints ?? defaultSkodaBreakpoints,
		originalBreakpoints ?? defaultSkodaBreakpoints);
	return createTheme(themeOverride
		? deepmerge(baseTheme, themeOverride(baseTheme), { arrayMerge: (dest, source, opts) => source })
		: baseTheme);
};
